import {
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc,
  query, where, getDoc, setDoc, serverTimestamp, runTransaction
} from 'firebase/firestore';
import { db } from '../firebase.js';

class EssayService {
  constructor() { this.collectionName = 'essays'; }

  // Temas ENEM
  async getEssayThemes() {
    return [
      {
        id: 1,
        title: "Democratização do acesso ao cinema no Brasil",
        description: "Discuta os desafios para democratizar o acesso ao cinema no Brasil e proponha soluções.",
        year: 2019
      },
      {
        id: 2,
        title: "O estigma associado às doenças mentais na sociedade brasileira",
        description: "Analise o estigma relacionado às doenças mentais no Brasil e suas consequências.",
        year: 2020
      },
      {
        id: 3,
        title: "Invisibilidade e registro civil: garantia de acesso à cidadania no Brasil",
        description: "Discuta a importância do registro civil para o exercício da cidadania.",
        year: 2021
      },
      {
        id: 4,
        title: "Desafios para a valorização de comunidades e povos tradicionais no Brasil",
        description: "Analise os desafios enfrentados por comunidades tradicionais brasileiras.",
        year: 2022
      },
      {
        id: 5,
        title: "Desafios para o enfrentamento da invisibilidade do trabalho de cuidado realizado pela mulher no Brasil",
        description: "Discuta a invisibilidade do trabalho de cuidado feminino e suas implicações.",
        year: 2023
      },
      {
        id: 6,
        title: "Desafios para a valorização da herança africana no Brasil",
        description: "Aborde obstáculos para que a cultura e o legado africanos sejam reconhecidos, valorizados e incorporados à identidade nacional.",
        year: 2024
      },
      {
        id: 7,
        title: "Perspectivas acerca do envelhecimento na sociedade brasileira",
        description: "Discuta aspectos sociais, culturais, políticos e econômicos do envelhecimento no Brasil e proponha intervenção que respeite os direitos humanos.",
        year: 2025
      }
    ];
  }

  async createEssay(essayData) {
    const now = Date.now();
    const payload = { ...essayData, createdAt: now, updatedAt: now };
    const ref = await addDoc(collection(db, this.collectionName), payload);
    return ref.id;
  }

  async getEssaysByStudent(studentId, limitCount = 12) {
    const col = collection(db, this.collectionName);
    let qy = query(col, where('studentId', '==', studentId));
    const snap = await getDocs(qy);
    let essays = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    essays.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    if (limitCount) essays = essays.slice(0, limitCount);
    return essays;
  }

  async getEssaysByClassroom(classroomId) {
    if (!classroomId) {
      throw new Error('Classroom ID is required');
    }

    const col = collection(db, this.collectionName);
    const qy = query(col, where('classroomId', '==', classroomId));
    const snap = await getDocs(qy);
    const essays = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    essays.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    return essays;
  }

  async getEssaysForStudents(studentIds = []) {
    if (!Array.isArray(studentIds) || studentIds.length === 0) {
      return [];
    }

    const uniqueIds = Array.from(new Set(studentIds.filter(Boolean)));
    if (uniqueIds.length === 0) {
      return [];
    }

    const col = collection(db, this.collectionName);
    const chunkSize = 10;
    const chunks = [];
    for (let index = 0; index < uniqueIds.length; index += chunkSize) {
      chunks.push(uniqueIds.slice(index, index + chunkSize));
    }

    const results = await Promise.all(chunks.map(async (chunk) => {
      const qy = query(col, where('studentId', 'in', chunk));
      const snap = await getDocs(qy);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }));

    const essays = results.flat();
    essays.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    return essays;
  }

  async getEssayById(essayId) {
    const ref = doc(db, this.collectionName, essayId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Essay not found');
    return { id: snap.id, ...snap.data() };
  }

  async updateEssayAnalysis(essayId, analysisData) {
    const ref = doc(db, this.collectionName, essayId);
    await updateDoc(ref, { analysis: analysisData, analyzedAt: Date.now(), updatedAt: Date.now() });
  }

  async deleteEssay(essayId) {
    await deleteDoc(doc(db, this.collectionName, essayId));
  }

  async getStudentProgress(studentId) {
    const essays = await this.getEssaysByStudent(studentId, null);
    const totalEssays = essays.length;

    const analyzedEssays = essays.filter(e => {
      const ts = e?.analysis?.totalScore;
      return typeof ts === 'number' && Number.isFinite(ts);
    });

    if (totalEssays === 0) {
      return { totalEssays: 0, averageScore: 0, progressTrend: [], competencyProgress: {}, recentImprovement: 0, analyzedEssays: 0 };
    }
    if (analyzedEssays.length === 0) {
      return { totalEssays, averageScore: 0, progressTrend: [], competencyProgress: {}, recentImprovement: 0, analyzedEssays: 0 };
    }

    const totalScore = analyzedEssays.reduce((s, e) => s + (e.analysis.totalScore || 0), 0);
    const averageScore = Math.round(totalScore / analyzedEssays.length);

    const recent = analyzedEssays.slice(0, 10);
    const progressTrend = recent.map(e => ({
      date: e.createdAt,
      score: e.analysis.totalScore || 0,
      title: e.title || 'Sem título'
    })).reverse();

    const competencyProgress = {};
    ['I', 'II', 'III', 'IV', 'V'].forEach(c => {
      const scores = analyzedEssays
        .filter(e => typeof e.analysis?.scores?.[c]?.score === 'number')
        .map(e => e.analysis.scores[c].score);
      if (scores.length > 0) {
        const avg = Math.round(scores.reduce((s, v) => s + v, 0) / scores.length);
        const latest = scores[0] || 0;
        const trend = this.calculateTrend(scores.slice(0, 5));
        competencyProgress[c] = { average: avg, latest, trend };
      }
    });

    let recentImprovement = 0;
    if (analyzedEssays.length >= 6) {
      const r3 = analyzedEssays.slice(0, 3);
      const p3 = analyzedEssays.slice(3, 6);
      const rAvg = r3.reduce((s, e) => s + (e.analysis.totalScore || 0), 0) / 3;
      const pAvg = p3.reduce((s, e) => s + (e.analysis.totalScore || 0), 0) / 3;
      recentImprovement = Math.round(rAvg - pAvg);
    }

    return { totalEssays, averageScore, progressTrend, competencyProgress, recentImprovement, analyzedEssays: analyzedEssays.length };
  }

  calculateTrend(scores) {
    if (scores.length < 2) return 'stable';
    const mid = Math.ceil(scores.length / 2);
    const recent = scores.slice(0, mid);
    const older = scores.slice(mid);
    const rAvg = recent.reduce((s, v) => s + v, 0) / recent.length;
    const oAvg = older.reduce((s, v) => s + v, 0) / older.length;
    const diff = rAvg - oAvg;
    if (diff > 10) return 'improving';
    if (diff < -10) return 'declining';
    return 'stable';
  }

  validateEssayData(essayData) {
    const errors = [];
    if (!essayData.studentId) errors.push('Student ID is required');
    if (!essayData.title || essayData.title.trim().length < 5) errors.push('Title must be at least 5 characters long');
    if (essayData.type === 'text') {
      const plain = essayData.contentPlain || (essayData.content ? essayData.content.replace(/<[^>]+>/g, '') : '');
      if (!plain || plain.trim().length < 100) errors.push('Essay content must be at least 100 characters long');
    } else if (essayData.type === 'image') {
      if (!essayData.imageData && !essayData.imageUrl) {
        errors.push('Image for image submissions');
      }
    } else {
      errors.push('Invalid essay type');
    }
    return { isValid: errors.length === 0, errors };
  }

  formatEssayForDisplay(essay) {
    const created = essay.createdAt ? new Date(essay.createdAt) : new Date();
    const wc = essay.content ? essay.content.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length : 0;

    const ts = essay?.analysis?.totalScore;
    const hasAnalysis = (typeof ts === 'number') && Number.isFinite(ts);
    const examType = essay?.examType || essay?.analysis?.examType || 'enem';
    const maxScore = essay?.analysis?.maxScore || this.getMaxScoreForExamType(examType);

    return {
      ...essay,
      formattedDate: created.toLocaleDateString('pt-BR'),
      formattedTime: created.toLocaleTimeString('pt-BR'),
      wordCount: wc,
      hasAnalysis,
      examType,
      maxScore,
      scoreColor: this.getScoreColor(hasAnalysis ? ts : 0, examType, maxScore)
    };
  }

  getMaxScoreForExamType(examType) {
    const maxScores = {
      enem: 1000,
      fuvest: 100,
      unicamp: 48,
      uerj: 20
    };
    return maxScores[examType] || 1000;
  }

  getScoreColor(score, examType = 'enem', maxScore = null) {
    if (!maxScore) {
      maxScore = this.getMaxScoreForExamType(examType);
    }
    
    // Convert to percentage
    const percentage = (score / maxScore) * 100;
    
    if (percentage >= 80) return '#28a745'; // excellent
    if (percentage >= 60) return '#17a2b8'; // good
    if (percentage >= 40) return '#ffc107'; // average
    return '#dc3545'; // poor
  }

  // ==========================================
  // USER CREDITS MANAGEMENT
  // ==========================================

  async ensureUserDocInitialized(userId, startingCredits = 5) {
    if (!userId) throw new Error('User ID is required');
    
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      await setDoc(userRef, {
        credits: startingCredits,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      });
      console.log(`User ${userId} initialized with ${startingCredits} credits`);
      return startingCredits;
    }
    
    const userData = userSnap.data();
    return userData.credits || 0;
  }

  async getUserCredits(userId) {
    if (!userId) throw new Error('User ID is required');
    
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return await this.ensureUserDocInitialized(userId);
    }
    
    const userData = userSnap.data();
    return userData.credits || 0;
  }

  async hasUserCredits(userId, requiredCredits = 1) {
    const currentCredits = await this.getUserCredits(userId);
    return currentCredits >= requiredCredits;
  }

  async addCredits(userId, amount = 2) {
    if (!userId) throw new Error('User ID is required');
    if (amount <= 0) throw new Error('Credit amount must be positive');
    
    const userRef = doc(db, 'users', userId);
    
    return await runTransaction(db, async (transaction) => {
      const userSnap = await transaction.get(userRef);
      
      if (!userSnap.exists()) {
        transaction.set(userRef, {
          credits: amount,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          last_topup_at: serverTimestamp()
        });
        return amount;
      } else {
        const userData = userSnap.data();
        const currentCredits = userData.credits || 0;
        const newCredits = currentCredits + amount;
        
        transaction.update(userRef, {
          credits: newCredits,
          updated_at: serverTimestamp(),
          last_topup_at: serverTimestamp()
        });
        
        console.log(`Added ${amount} credits to user ${userId}. New balance: ${newCredits}`);
        return newCredits;
      }
    });
  }

  async decrementCredits(userId, amount = 1) {
    if (!userId) throw new Error('User ID is required');
    if (amount <= 0) throw new Error('Credit amount must be positive');
    
    const userRef = doc(db, 'users', userId);
    
    return await runTransaction(db, async (transaction) => {
      const userSnap = await transaction.get(userRef);
      
      if (!userSnap.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userSnap.data();
      const currentCredits = userData.credits || 0;
      
      if (currentCredits < amount) {
        throw new Error(`Insufficient credits. Current: ${currentCredits}, Required: ${amount}`);
      }
      
      const newCredits = currentCredits - amount;
      transaction.update(userRef, {
        credits: newCredits,
        updated_at: serverTimestamp()
      });
      
      console.log(`Decremented ${amount} credits from user ${userId}. New balance: ${newCredits}`);
      return newCredits;
    });
  }

  // ==========================================
  // MULTI-EXAM NORMALIZATION
  // ==========================================

  normalizeAnalysisData(analysisData, options = {}) {
    const { computeTotalIfMissing = true, examType = 'enem' } = options;
    
    if (!analysisData) return null;
    
    const applyLeniencyToScores = (scoreMap) => scoreMap;
    
    const normalizeParagraphFeedback = (list) => {
      if (!Array.isArray(list)) return [];
      return list
        .map((item, idx) => {
          if (!item) return null;
          if (typeof item === 'string') {
            const text = item.trim();
            if (!text) return null;
            const matchNum = text.match(/^(\d+)\s*[-:]\s*(.+)$/);
            return {
              paragraph: matchNum ? Number(matchNum[1]) : idx + 1,
              summary: matchNum ? matchNum[2].trim() : text,
              improvement: ''
            };
          }

          const paragraph =
            Number.isFinite(item.paragraph) ? Number(item.paragraph) :
            Number.isFinite(item.index) ? Number(item.index) + 1 :
            Number.isFinite(item.order) ? Number(item.order) :
            Number.isFinite(item.position) ? Number(item.position) : idx + 1;

          const summary = [item.summary, item.focus, item.idea, item.role]
            .find((v) => typeof v === 'string' && v.trim()) || '';
          const strength = [item.strength, item.positive, item.bestPart, item.highlight]
            .find((v) => typeof v === 'string' && v.trim()) || '';
          const improvement = [item.improvement, item.action, item.fix, item.nextStep, item.advice]
            .find((v) => typeof v === 'string' && v.trim()) || '';
          const snippet = [item.snippet, item.excerpt, item.quote, item.referenceText]
            .find((v) => typeof v === 'string' && v.trim()) || '';

          if (!summary && !improvement && !strength) return null;

          return {
            paragraph: Math.max(1, Math.round(paragraph)),
            summary: summary.trim(),
            strength: strength.trim(),
            improvement: improvement.trim(),
            snippet: snippet.trim()
          };
        })
        .filter((p) => p && (p.summary || p.improvement))
        .sort((a, b) => a.paragraph - b.paragraph);
    };

    
    
    let parsed = analysisData;
    if (typeof analysisData === 'string') {
      try {
        parsed = JSON.parse(analysisData);
      } catch (e) {
        console.error('Failed to parse analysis data:', e);
        return null;
      }
    }
    
    // Detect exam type from data if not provided
    let detectedExamType = examType;
    if (!examType || examType === 'enem') {
      if (parsed.scores?.content || parsed.scores?.structure || parsed.scores?.language) {
        detectedExamType = 'fuvest';
      } else if (parsed.scores?.content_and_argumentation || parsed.genre) {
        detectedExamType = 'unicamp';
      } else if (parsed.scores?.understanding_of_prompt) {
        detectedExamType = 'uerj';
      }
    }
    
    let scores = {};
    let totalScore = parsed.totalScore || parsed.total || 0;
    let maxScore = this.getMaxScoreForExamType(detectedExamType);
    const modelName = (typeof parsed.model === 'string' && parsed.model.trim()) ? parsed.model.trim() : null;
    const rawTotalPenalty = Number.isFinite(Number(parsed.totalPenalty)) ? Number(parsed.totalPenalty) : 0;

    const normalizePenaltyTable = (list) => {
      if (!Array.isArray(list)) return [];
      return list
        .map((item, idx) => {
          if (!item) return null;
          const points = Number(item.points);
          return {
            id: item.id || `penalty-${idx}`,
            type: typeof item.type === 'string' ? item.type : 'penalty',
            description: typeof item.description === 'string' ? item.description : '',
            snippet: typeof item.snippet === 'string' ? item.snippet : '',
            points: Number.isFinite(points) ? points : 0,
            category: item.category || item.competency || item.area || ''
          };
        })
        .filter(Boolean);
    };

    const normalizedPenaltyTable = normalizePenaltyTable(parsed.penaltyTable || parsed.penalties || parsed.penaltySummary || []);
    const penaltySum = normalizedPenaltyTable.reduce((sum, p) => sum + (Number(p.points) || 0), 0);
    
    switch (detectedExamType) {
      case 'fuvest':
        scores = this._normalizeFuvestScores(parsed.scores);
        break;
        
      case 'unicamp':
        scores = this._normalizeUnicampScores(parsed.scores);
        break;
        
      case 'uerj':
        scores = this._normalizeUerjScores(parsed.scores);
        break;
        
      case 'enem':
      default:
        scores = parsed.scores;
        break;
    }
    
    scores = applyLeniencyToScores(scores, detectedExamType);
    const recomputedTotal = Object.values(scores).reduce((sum, comp) => sum + (comp.score || 0), 0);
    const expectedTotal = Math.max(0, Math.min(maxScore, recomputedTotal - penaltySum));
    let penaltyMismatch = false;
    if (!Number.isFinite(totalScore) || computeTotalIfMissing) {
      totalScore = expectedTotal;
    } else if (totalScore !== expectedTotal || rawTotalPenalty !== penaltySum) {
      penaltyMismatch = true;
      totalScore = expectedTotal;
    }
    const totalPenalty = penaltySum || rawTotalPenalty;
    
    totalScore = Math.max(0, Math.min(maxScore, Number(totalScore) || 0));
    
    const normalized = {
      scores,
      totalScore,
      totalPenalty,
      maxScore,
      examType: detectedExamType,
      comments: parsed.comments || parsed.feedback || parsed.feedbackSummary || '',
      model: modelName,
      raw: parsed
    };

    if (detectedExamType === 'unicamp' && parsed.genre) {
      normalized.genre = parsed.genre;
    }

    if (parsed.inlineErrors) normalized.inlineErrors = parsed.inlineErrors;
    if (parsed.problems) normalized.problems = parsed.problems;
    if (parsed.suggestions) normalized.suggestions = parsed.suggestions;
    if (parsed.correctedVersion) normalized.correctedVersion = parsed.correctedVersion;
    let strengths = [];
    if (Array.isArray(parsed.strengths)) {
      strengths = parsed.strengths
        .map((item) => {
          if (typeof item === 'string') return item.trim();
          if (item && typeof item === 'object') {
            const maybeText = item.text || item.value || item.label;
            if (typeof maybeText === 'string') return maybeText.trim();
          }
          return '';
        })
        .filter(Boolean);
      if (strengths.length) {
        normalized.strengths = strengths;
      } else if (parsed.strengths.length) {
        console.warn('[EssayService] Guided feedback: strengths provided but none survived normalization', {
          sample: parsed.strengths.slice(0, 2)
        });
      }
    } else if (parsed.strengths) {
      console.info('[EssayService] Guided feedback: strengths field present but not an array', {
        type: typeof parsed.strengths,
        preview: parsed.strengths
      });
    }

    let repertorio = [];
    if (Array.isArray(parsed.repertorioSociocultural)) {
      repertorio = parsed.repertorioSociocultural
        .filter((item) => item && (typeof item === 'object' || typeof item === 'string'))
        .map((item) => {
          if (typeof item === 'string') {
            const cleaned = item.trim();
            if (!cleaned) return { reference: '', application: '' };
            const separators = [' — ', ' - ', ' – ', ':'];
            for (const sep of separators) {
              if (cleaned.includes(sep)) {
                const [ref, app] = cleaned.split(sep);
                return {
                  reference: (ref || '').trim(),
                  application: (app || '').trim()
                };
              }
            }
            return { reference: cleaned, application: '' };
          }

          const referenceCandidates = [
            item.reference,
            item.topic,
            item.title,
            item.example,
            item.source,
            item.label
          ];
          const applicationCandidates = [
            item.application,
            item.use,
            item.usage,
            item.useCase,
            item.howToUse,
            item.context,
            item.explanation,
            item.reason,
            item.link
          ];

          const reference = referenceCandidates.find((val) => typeof val === 'string' && val.trim()) || '';
          const application = applicationCandidates.find((val) => typeof val === 'string' && val.trim()) || '';

          return {
            reference: reference.trim(),
            application: application.trim()
          };
        })
        .filter((item) => item.reference && item.application);
      if (!repertorio.length && parsed.repertorioSociocultural.some((entry) => typeof entry === 'string')) {
        console.info('[EssayService] Guided feedback: repertório strings detected but lacked separators', {
          sample: parsed.repertorioSociocultural.slice(0, 2)
        });
      }
      if (repertorio.length) {
        normalized.repertorioSociocultural = repertorio;
      } else if (parsed.repertorioSociocultural.length) {
        console.warn('[EssayService] Guided feedback: repertório entries dropped during normalization', {
          sample: parsed.repertorioSociocultural.slice(0, 2)
        });
      }
    } else if (parsed.repertorioSociocultural) {
      console.info('[EssayService] Guided feedback: repertorioSociocultural present but not array', {
        type: typeof parsed.repertorioSociocultural,
        preview: parsed.repertorioSociocultural
      });
    }

    let exemplars = [];
    if (Array.isArray(parsed.exemplarSentences)) {
      exemplars = parsed.exemplarSentences
        .filter((item) => item && (typeof item === 'object' || typeof item === 'string'))
        .map((item) => {
          if (typeof item === 'string') {
            const cleaned = item.trim();
            if (!cleaned) return { excerpt: '', whyItWorks: '' };
            const separators = [' — ', ' - ', ' – ', ':'];
            for (const sep of separators) {
              if (cleaned.includes(sep)) {
                const [excerpt, why] = cleaned.split(sep);
                return {
                  excerpt: (excerpt || '').trim(),
                  whyItWorks: (why || '').trim()
                };
              }
            }
            return { excerpt: cleaned, whyItWorks: '' };
          }

          const excerptCandidates = [
            item.excerpt,
            item.quote,
            item.text,
            item.sentence,
            item.example
          ];
          const whyCandidates = [
            item.whyItWorks,
            item.reason,
            item.analysis,
            item.explanation,
            item.commentary,
            item.justification
          ];

          const excerpt = excerptCandidates.find((val) => typeof val === 'string' && val.trim()) || '';
          const whyItWorks = whyCandidates.find((val) => typeof val === 'string' && val.trim()) || '';

          return {
            excerpt: excerpt.trim(),
            whyItWorks: whyItWorks.trim()
          };
        })
        .filter((item) => item.excerpt && item.whyItWorks);
      if (!exemplars.length && parsed.exemplarSentences.some((entry) => typeof entry === 'string')) {
        console.info('[EssayService] Guided feedback: exemplar sentences string format detected without delimiter', {
          sample: parsed.exemplarSentences.slice(0, 2)
        });
      }
      if (exemplars.length) {
        normalized.exemplarSentences = exemplars;
      } else if (parsed.exemplarSentences.length) {
        console.warn('[EssayService] Guided feedback: exemplar sentences dropped during normalization', {
          sample: parsed.exemplarSentences.slice(0, 2)
        });
      }
    } else if (parsed.exemplarSentences) {
      console.info('[EssayService] Guided feedback: exemplarSentences present but not array', {
        type: typeof parsed.exemplarSentences,
        preview: parsed.exemplarSentences
      });
    }

    let roadmap = [];
    if (Array.isArray(parsed.roadmapTo1000)) {
      roadmap = parsed.roadmapTo1000
        .map((item) => {
          if (typeof item === 'string') return item.trim();
          if (item && typeof item === 'object') {
            const maybe = item.step || item.text || item.tip;
            if (typeof maybe === 'string') return maybe.trim();
          }
          return '';
        })
        .filter(Boolean);
      if (roadmap.length) {
        normalized.roadmapTo1000 = roadmap;
      } else if (parsed.roadmapTo1000.length) {
        console.warn('[EssayService] Guided feedback: roadmap entries dropped during normalization', {
          sample: parsed.roadmapTo1000.slice(0, 2)
        });
      }
    } else if (parsed.roadmapTo1000) {
      console.info('[EssayService] Guided feedback: roadmapTo1000 present but not array', {
        type: typeof parsed.roadmapTo1000,
        preview: parsed.roadmapTo1000
      });
    }

    if (strengths.length || repertorio.length || exemplars.length || roadmap.length) {
      console.info('[EssayService] Guided feedback counts', {
        strengths: strengths.length,
        repertorio: repertorio.length,
        exemplars: exemplars.length,
        roadmap: roadmap.length
      });
    }

    const paragraphFeedback = normalizeParagraphFeedback(
      parsed.paragraphFeedback || parsed.paragraph_analysis || parsed.paragraphs || []
    );
    if (paragraphFeedback.length) {
      normalized.paragraphFeedback = paragraphFeedback;
    } else if (parsed.paragraphFeedback) {
      console.info('[EssayService] paragraphFeedback present but empty after normalization', {
        sample: Array.isArray(parsed.paragraphFeedback) ? parsed.paragraphFeedback.slice(0, 2) : parsed.paragraphFeedback
      });
    }

    if (normalizedPenaltyTable.length) {
      console.info('[EssayService] penaltyTable normalized', {
        count: normalizedPenaltyTable.length,
        sample: normalizedPenaltyTable.slice(0, 2)
      });
      normalized.penaltyTable = normalizedPenaltyTable;
    } else if (parsed.penaltyTable || parsed.penalties || parsed.penaltySummary) {
      console.warn('[EssayService] penaltyTable present but empty after normalization', {
        penaltyTable: parsed.penaltyTable,
        penalties: parsed.penalties,
        penaltySummary: parsed.penaltySummary
      });
    }
    if (penaltyMismatch) {
      normalized.penaltyMismatch = true;
      console.warn('[EssayService] penalty mismatch detected', {
        recomputedTotal,
        penaltySum,
        rawTotalPenalty,
        expectedTotal
      });
    }

    return normalized;
  }
  
  _normalizeFuvestScores(scoresData) {
    const scores = {};
    const criteria = {
      content: { max: 40, label: 'Conteúdo / Argumentação' },
      structure: { max: 35, label: 'Estrutura / Coesão' },
      language: { max: 25, label: 'Linguagem / Modalidade Escrita' }
    };
    
    Object.entries(criteria).forEach(([key, config]) => {
      const scoreData = scoresData?.[key];
      if (scoreData) {
        const score = Math.max(0, Math.min(config.max, Number(scoreData.score) || 0));
        scores[key] = {
          score,
          maxScore: config.max,
          label: config.label,
          justification: scoreData.justification || scoreData.explanation || ''
        };
      } else {
        scores[key] = { 
          score: 0, 
          maxScore: config.max,
          label: config.label,
          justification: 'Não avaliado' 
        };
      }
    });
    
    return scores;
  }
  
  _normalizeUnicampScores(scoresData) {
    const scores = {};
    const criteria = {
      content_and_argumentation: { max: 12, label: 'Conteúdo e Argumentação' },
      genre_adequacy: { max: 12, label: 'Adequação ao Gênero' },
      structure_and_cohesion: { max: 12, label: 'Estrutura e Coesão' },
      language_and_register: { max: 12, label: 'Linguagem e Registro' }
    };
    
    Object.entries(criteria).forEach(([key, config]) => {
      const scoreData = scoresData?.[key];
      if (scoreData) {
        const score = Math.max(0, Math.min(config.max, Number(scoreData.score) || 0));
        scores[key] = {
          score,
          maxScore: config.max,
          label: config.label,
          justification: scoreData.justification || scoreData.explanation || ''
        };
      } else {
        scores[key] = { 
          score: 0, 
          maxScore: config.max,
          label: config.label,
          justification: 'Não avaliado' 
        };
      }
    });
    
    return scores;
  }
  
  _normalizeUerjScores(scoresData) {
    const scores = {};
    const criteria = {
      understanding_of_prompt: { max: 5, label: 'Compreensão da Proposta' },
      argumentative_quality: { max: 5, label: 'Qualidade Argumentativa' },
      textual_cohesion: { max: 5, label: 'Coesão Textual' },
      formal_language_control: { max: 5, label: 'Controle da Norma Formal' }
    };
    
    Object.entries(criteria).forEach(([key, config]) => {
      const scoreData = scoresData?.[key];
      if (scoreData) {
        const score = Math.max(0, Math.min(config.max, Number(scoreData.score) || 0));
        scores[key] = {
          score,
          maxScore: config.max,
          label: config.label,
          justification: scoreData.justification || scoreData.explanation || ''
        };
      } else {
        scores[key] = { 
          score: 0, 
          maxScore: config.max,
          label: config.label,
          justification: 'Não avaliado' 
        };
      }
    });
    
    return scores;
  }

  async updateEssayAnalysisNormalized(essayId, analysisData, options = {}) {
    const normalized = this.normalizeAnalysisData(analysisData, options);
    if (!normalized) {
      throw new Error('Invalid analysis data provided');
    }
    
    const ref = doc(db, this.collectionName, essayId);
    await updateDoc(ref, { 
      analysis: normalized, 
      analyzedAt: Date.now(), 
      updatedAt: Date.now() 
    });
    
    return normalized;
  }

  async updateEssayAnalysisAndSpendCredit({ userId, essayId, analysisData, computeTotalIfMissing = true, examType = 'enem', imageUrl = null }) {
    if (!userId) throw new Error('User ID is required');
    if (!essayId) throw new Error('Essay ID is required');
    if (!analysisData) throw new Error('Analysis data is required');
    
    const normalized = this.normalizeAnalysisData(analysisData, { computeTotalIfMissing, examType });
    if (!normalized) {
      throw new Error('Invalid analysis data - could not normalize');
    }
    
    const hasValidScores = Object.values(normalized.scores).some(comp => comp.score >= 0);
    if (!hasValidScores) {
      throw new Error('Analysis appears invalid - no valid competency scores found');
    }
    
    const userRef = doc(db, 'users', userId);
    const essayRef = doc(db, this.collectionName, essayId);
    
    return await runTransaction(db, async (transaction) => {
      const essaySnap = await transaction.get(essayRef);
      if (!essaySnap.exists()) {
        throw new Error('Essay not found');
      }
      
      const essayData = essaySnap.data();
      if (essayData.analysis && typeof essayData.analysis.totalScore === 'number') {
        console.log(`Essay ${essayId} already has analysis, skipping credit deduction`);
        return { 
          success: true, 
          creditsRemaining: null, 
          alreadyAnalyzed: true,
          analysis: essayData.analysis 
        };
      }
      
      const userSnap = await transaction.get(userRef);
      if (!userSnap.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userSnap.data();
      const currentCredits = userData.credits || 0;
      
      if (currentCredits < 1) {
        throw new Error('NO_CREDITS');
      }
      
      const patch = {
        analysis: normalized,
        analyzedAt: Date.now(),
        updatedAt: Date.now()
      };
      if (imageUrl) {
        patch.imageUrl = imageUrl;
      }
      transaction.update(essayRef, patch);
      
      const newCredits = currentCredits - 1;
      transaction.update(userRef, {
        credits: newCredits,
        updated_at: serverTimestamp()
      });
      
      console.log(`Successfully analyzed essay ${essayId} and spent 1 credit for user ${userId}. Credits remaining: ${newCredits}`);
      
      return { 
        success: true, 
        creditsRemaining: newCredits, 
        alreadyAnalyzed: false,
        analysis: normalized 
      };
    });
  }
}

const essayService = new EssayService();
export default essayService;
